import { Button, Group, Select } from '@mantine/core';
import { useMutation, useQuery } from '@tanstack/react-query';
import React, { useState } from 'react';
import styled from 'styled-components';
import { Page } from './components/Page';
import { PageCard } from './components/PageCard';
import { PageCardLogo } from './components/PageCardLogo';
import { authClient } from './hooks/client';
import { useAccountListQuery } from './hooks/useAccountListQuery';
import { useAppleTouchIconFallback } from './hooks/useAppleTouchIconFallback';
import { Colors } from './patterns/Colors';

// The oauth-provider redirects here with the full signed authorization query
// (client_id, scope, redirect_uri, ..., exp, sig). Echoing it back on the consent
// POST is what lets better-auth resume the authorization transaction.
const readAuthorizationQuery = () => {
    const params = new URLSearchParams(window.location.search);
    return {
        clientId: params.get('client_id') ?? '',
        scope: params.get('scope') ?? '',
        redirectUri: params.get('redirect_uri'),
        oauthQuery: window.location.search.slice(1),
    };
};

interface PublicClient {
    name: string | null;
    logoUri: string | null;
}

// Response uses RFC 7591 field names (client_name, logo_uri, client_uri, ...)
const useOAuthClient = (clientId: string) =>
    useQuery({
        queryKey: ['oauth2.public-client', clientId],
        enabled: !!clientId,
        queryFn: async (): Promise<PublicClient> => {
            const res = await fetch(`/auth/oauth2/public-client?client_id=${encodeURIComponent(clientId)}`, {
                credentials: 'same-origin',
                headers: { accept: 'application/json' },
            });
            if (!res.ok) return { name: null, logoUri: null };
            const data = await res.json();
            return {
                name: data.client_name ?? null,
                logoUri: data.logo_uri ?? null,
            };
        },
    });

const useConsentMutation = (scope: string, oauthQuery: string, accountId: string | null) =>
    useMutation({
        mutationKey: ['oauth2.consent'],
        mutationFn: async (accept: boolean) => {
            // Stage the account this authorization binds to before consenting —
            // the oauth-provider's consentReferenceId hook reads it back server-side.
            // A failed staging fails the flow: consent must not proceed bound to
            // an account the user didn't pick.
            if (accept && accountId) {
                const staged = await fetch('/api/v2/me.oauthConsent.stageAccount', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'same-origin',
                    body: JSON.stringify({ accountId }),
                });
                if (!staged.ok) throw new Error(`Failed to select account (${staged.status})`);
            }
            const res = await fetch('/auth/oauth2/consent', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'same-origin',
                // eslint-disable-next-line camelcase
                body: JSON.stringify({ accept, scope, oauth_query: oauthQuery }),
            });
            const data = await res.json();
            const url = data.url ?? data.redirect_uri;
            if (!url) throw new Error('No redirect returned');
            return url as string;
        },
        onSuccess: (url) => {
            window.location.href = url;
        },
    });

export const OAuthConsentPage = () => {

    const { clientId, scope, redirectUri, oauthQuery } = readAuthorizationQuery();
    const { data: client } = useOAuthClient(clientId);
    const clientName = client?.name ?? null;
    const icon = useAppleTouchIconFallback(client?.logoUri, redirectUri);
    const { data: session } = authClient.useSession();

    // Which account the authorization binds to. Staged server-side on Authorize
    // so the grant's reference_id (and the accountId token claim) carry it. The
    // list is filtered server-side to MCP-eligible orgs — active (not read-only),
    // member-or-greater (not a guest), and MCP not switched off — so an empty
    // result means there's nowhere the user can connect an AI tool to.
    const { data: accounts, isLoading: accountsLoading, error: accountsError } =
        useAccountListQuery({ active: true, member: true, mcp: true });
    const [accountId, setAccountId] = useState<string | null>(null);
    const account =
        accounts?.find(x => x.id === accountId) ??
        accounts?.find(x => x.isDefault) ??
        accounts?.[0];

    // No eligible org → the user can't authorize anything; we surface a message
    // and leave only Cancel enabled.
    const noEligibleAccounts = !accountsLoading && !accountsError && (accounts?.length ?? 0) === 0;

    const consent = useConsentMutation(scope, oauthQuery, account?.id ?? null);
    // Keep buttons disabled while redirecting after success; variables holds the
    // accept flag of the in-flight decision, so only the clicked button spins.
    const busy = consent.isPending || consent.isSuccess;
    const authorizing = busy && consent.variables === true;

    const name = clientName ?? 'This application';

    return <Page>
        <PageCard>
            <PageCardLogo />
            <PageCard.Title>Authorize application</PageCard.Title>
            <PageCard.Copy>
                {icon && <ClientLogo src={icon} alt="" />}
                <ClientName>{name}</ClientName> will be able to:
            </PageCard.Copy>

            <CapabilityList>
                <li>Confirm who you are</li>
                <li>See your profile — name, photo, and email</li>
                <li>See your workspaces and boards</li>
                <li>Read, edit, and create boards on your behalf</li>
            </CapabilityList>

            <Group grow mt="md">
                <Button variant="default" disabled={busy} onClick={() => consent.mutate(false)}>
                    Cancel
                </Button>
                {/* Wait for the account list so consent binds to a deliberate choice
                    (on fetch error the server falls back to the default account).
                    With no MCP-eligible org there's nothing to bind to, so stay disabled. */}
                <Button loading={authorizing} disabled={(busy && !authorizing) || accountsLoading || noEligibleAccounts} onClick={() => consent.mutate(true)}>
                    {clientName ? `Authorize ${clientName}` : 'Authorize'}
                </Button>
            </Group>

            {consent.isError &&
                <ErrorText>Something went wrong. Please try again.</ErrorText>
            }

            {accountsError &&
                <ErrorText>Couldn't load your organizations. Please try again.</ErrorText>
            }

            {noEligibleAccounts
                ? <NoticeText>
                    You're not in an organization that can be connected to MCP right now.
                    MCP access needs an organization where you're a full member, MCP is enabled,
                    and the plan isn't read-only. Ask an owner to enable MCP, or switch to an
                    organization that supports it.
                </NoticeText>
                : session?.user && (accounts && accounts.length > 1
                    ? <AuthorizingAs>
                        <span>Authorizing as {session.user.email} for</span>
                        <Select
                            data={accounts.map(x => ({ value: x.id, label: x.label }))}
                            value={account?.id ?? null}
                            onChange={setAccountId}
                            allowDeselect={false}
                            size="xs"
                            w="14rem"
                            aria-label="Organization to authorize for"
                        />
                    </AuthorizingAs>
                    : <SignedInAs>
                        {account
                            ? <>Authorizing as {session.user.email} for <AccountName>{account.label}</AccountName></>
                            : <>Signed in as {session.user.email}</>}
                    </SignedInAs>
                )}
        </PageCard>
    </Page>;
};

const ClientName = styled.span`
    font-weight: 600;
    color: ${Colors.text};
`;

const ClientLogo = styled.img`
    width: 1.25rem;
    height: 1.25rem;
    border-radius: 0.25rem;
    vertical-align: text-bottom;
    margin-right: 0.375rem;
`;

const CapabilityList = styled.ul`
    margin: 1rem 0 0.5rem;
    padding-left: 1.25rem;
    font-size: 0.875rem;
    color: ${Colors.textLight};

    li {
        margin-bottom: 0.25rem;
    }
`;

const ErrorText = styled.p`
    font-size: 0.875rem;
    color: #b91c1c;
    margin: 1rem 0 0;
    text-align: center;
`;

const SignedInAs = styled.p`
    font-size: 0.75rem;
    color: ${Colors.textLight};
    margin: 1.5rem 0 0;
    text-align: center;
`;

const NoticeText = styled.p`
    font-size: 0.8125rem;
    line-height: 1.4;
    color: ${Colors.text};
    background: ${Colors.borderLight};
    border: 1px solid ${Colors.border};
    border-radius: 0.5rem;
    padding: 0.75rem 0.875rem;
    margin: 1.5rem 0 0;
`;

const AuthorizingAs = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.375rem;
    font-size: 0.75rem;
    color: ${Colors.textLight};
    margin-top: 1.5rem;
`;

const AccountName = styled.span`
    font-weight: 600;
`;
